import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { AppContext } from "../../context";
import {
  createStoreBroadcast,
  getStoreBroadcast,
  getStoreBroadcasts,
  refreshStoreBroadcastStatus,
} from "../sales/service";
import { fmtRelativeTime, toErrorText } from "./storeSalesDashboardHelpers";

const BROADCAST_TEMPLATE_DATALIST_ID = "broadcast-template-suggestions";

const REFRESHABLE_DELIVERY_STATUSES = ["queued", "sent", "delivered"];

const canRefreshNotification = (notification) => {
  if (notification?.id == null) return false;

  return REFRESHABLE_DELIVERY_STATUSES.includes(notification.delivery_status);
};

const broadcastHasRefreshableNotifications = (broadcast) =>
  (broadcast?.recipients || []).some((recipient) =>
    canRefreshNotification(recipient.notification)
  );

export const BroadcastSendModal = ({
  isOpen,
  toggle,
  selectedCount,
  storeUserIds,
  criteria,
  templateOptions = [],
  onSuccess,
}) => {
  const [template, setTemplate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    setTemplate("");
    toggle();
  };

  const handleSubmit = async () => {
    const trimmed = String(template || "").trim();
    if (!trimmed) {
      setError("Enter or select a template.");
      return;
    }
    if (!Array.isArray(storeUserIds) || storeUserIds.length === 0) {
      setError("No users selected.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await createStoreBroadcast({
        template: trimmed,
        criteria,
        store_user_ids: storeUserIds,
      });
      onSuccess?.();
      setTemplate("");
      toggle();
    } catch (e) {
      setError(toErrorText(e, "Unable to send broadcast."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>Send message</ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Send WhatsApp template to {selectedCount} selected user
          {selectedCount === 1 ? "" : "s"}.
        </p>
        <label className="form-label small text-muted mb-1">Template</label>
        <Input
          type="text"
          bsSize="sm"
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          disabled={submitting}
          className="unmask"
          list={BROADCAST_TEMPLATE_DATALIST_ID}
          placeholder="Type or pick a template id"
          autoComplete="off"
        />
        <datalist id={BROADCAST_TEMPLATE_DATALIST_ID}>
          {templateOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </datalist>
        <p className="text-muted small mt-1 mb-0">
          Suggestions from past broadcasts; you can type a new template id.
        </p>
        {error ? (
          <Alert color="danger" className="mt-3 mb-0" fade={false}>
            {error}
          </Alert>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          outline
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <Spinner size="sm" className="me-1" /> Sending…
            </>
          ) : (
            "Send"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const StoreBroadcastsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    loading: false,
    error: null,
    refreshError: null,
    broadcast: null,
  });
  const [refreshingBroadcast, setRefreshingBroadcast] = useState(false);

  const loadBroadcasts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await getStoreBroadcasts();
      setBroadcasts(
        Array.isArray(payload?.broadcasts) ? payload.broadcasts : []
      );
    } catch (e) {
      setError(toErrorText(e, "Unable to load broadcasts."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBroadcasts();
  }, [currentUser?.admin, loadBroadcasts, navigate]);

  const openBroadcastDetail = async (broadcastId) => {
    setDetailModal({
      isOpen: true,
      loading: true,
      error: null,
      refreshError: null,
      broadcast: null,
    });
    try {
      const payload = await getStoreBroadcast(broadcastId);
      setDetailModal({
        isOpen: true,
        loading: false,
        error: null,
        refreshError: null,
        broadcast: payload?.broadcast ?? null,
      });
    } catch (e) {
      setDetailModal({
        isOpen: true,
        loading: false,
        error: toErrorText(e, "Unable to load broadcast details."),
        refreshError: null,
        broadcast: null,
      });
    }
  };

  const closeDetailModal = () => {
    setRefreshingBroadcast(false);
    setDetailModal({
      isOpen: false,
      loading: false,
      error: null,
      refreshError: null,
      broadcast: null,
    });
  };

  const handleRefreshBroadcast = async (broadcastId) => {
    if (broadcastId == null) return;

    setRefreshingBroadcast(true);
    setDetailModal((prev) => ({ ...prev, refreshError: null }));
    try {
      const payload = await refreshStoreBroadcastStatus(broadcastId);
      const broadcast = payload?.broadcast;
      if (!broadcast?.id) {
        throw new Error("Invalid refresh response.");
      }
      setDetailModal((prev) => ({
        ...prev,
        broadcast,
      }));
    } catch (e) {
      setDetailModal((prev) => ({
        ...prev,
        refreshError: toErrorText(e, "Unable to refresh broadcast statuses."),
      }));
    } finally {
      setRefreshingBroadcast(false);
    }
  };

  if (!currentUser?.admin) {
    return null;
  }

  return (
    <div className="container py-4">
      <h1 className="h4 mb-4">Broadcasts</h1>

      {error ? (
        <Alert color="danger" fade={false}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="py-5 text-center">
          <Spinner />
        </div>
      ) : broadcasts.length === 0 ? (
        <p className="text-muted">No broadcasts yet.</p>
      ) : (
        <Table bordered hover responsive className="mb-0 store-sales-table">
          <thead className="store-sales-table__head">
            <tr>
              <th>Template</th>
              <th>Recipients</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {broadcasts.map((row) => (
              <tr
                key={row.id}
                style={{ cursor: "pointer" }}
                onClick={() => openBroadcastDetail(row.id)}
              >
                <td className="font-monospace small">{row.template}</td>
                <td>{row.recipient_count ?? 0}</td>
                <td title={row.created_at}>
                  {fmtRelativeTime(row.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal isOpen={detailModal.isOpen} toggle={closeDetailModal} size="lg">
        <ModalHeader toggle={closeDetailModal}>
          Broadcast
          {detailModal.broadcast?.template
            ? `: ${detailModal.broadcast.template}`
            : ""}
        </ModalHeader>
        <ModalBody>
          {detailModal.loading ? (
            <div className="py-4 text-center">
              <Spinner />
            </div>
          ) : detailModal.error ? (
            <Alert color="danger" fade={false}>
              {detailModal.error}
            </Alert>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <p className="text-muted small mb-0">
                  Sent {fmtRelativeTime(detailModal.broadcast?.created_at)}
                </p>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  disabled={
                    refreshingBroadcast ||
                    !broadcastHasRefreshableNotifications(detailModal.broadcast)
                  }
                  onClick={() =>
                    handleRefreshBroadcast(detailModal.broadcast?.id)
                  }
                >
                  {refreshingBroadcast ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Refreshing…
                    </>
                  ) : (
                    "Refresh"
                  )}
                </Button>
              </div>
              {detailModal.refreshError ? (
                <Alert color="danger" className="mb-3" fade={false}>
                  {detailModal.refreshError}
                </Alert>
              ) : null}
              <Table bordered responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Buyer</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailModal.broadcast?.recipients || []).map((r) => (
                    <tr key={r.store_user_id ?? `${r.mobile}-${r.name}`}>
                      <td>{r.name || "—"}</td>
                      <td>{r.mobile || "—"}</td>
                      <td>{r.buyer_name || "—"}</td>
                      <td className="text-capitalize">
                        {r.notification?.delivery_status || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default StoreBroadcastsPage;
